import React, { useState } from 'react';
import Select from 'react-select';
import { customSelectStylesLarge } from '../../../config/selectStyles';
import { showMessage } from '../../../scripts/showMessage.js';

const labelStyle = {
    display: 'block',
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: 'var(--text-secondary, #64748b)'
};

const inputStyle = {
    width: '100%',
    padding: '0.625rem 0.875rem',
    border: '1px solid var(--border-medium, #cbd5e1)',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    color: 'var(--text-primary, #2c3e50)',
    background: 'var(--bg-card, #ffffff)',
    transition: 'border-color 0.2s ease',
    outline: 'none'
};

const digitOptions = [
    { value: 2, label: '2-stellig' },
    { value: 3, label: '3-stellig' },
    { value: 4, label: '4-stellig' },
    { value: 5, label: '5-stellig' },
    { value: 6, label: '6-stellig' },
    { value: 7, label: '7-stellig' },
    { value: 8, label: '8-stellig' }
];

const variables = [
    { name: 'Jahr (vierstellig)', input: '{jjjj}', example: '2026' },
    { name: 'Jahr (zweistellig)', input: '{jj}', example: '26' },
    { name: 'Monat (einstellig)', input: '{m}', example: '1' },
    { name: 'Monat (zweistellig)', input: '{mm}', example: '01' },
    { name: 'Tag (einstellig)', input: '{t}', example: '1' },
    { name: 'Tag (zweistellig)', input: '{tt}', example: '01' }
];

const StepNumberCircles = ({ initialData, onComplete }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        angebot_zusatz: initialData?.offerAddition || '',
        angebotsnummer: initialData?.offerNumber || 1,
        anzstellenang: digitOptions.find(d => d.value === (initialData?.offerDigits || 4)) || digitOptions[2],
        auftrag_zusatz: initialData?.orderConfirmationAddition || '',
        auftragsnummer: initialData?.orderConfirmationNumber || 1,
        anzstellenab: digitOptions.find(d => d.value === (initialData?.orderConfirmationDigits || 4)) || digitOptions[2],
        rechnung_zusatz: initialData?.invoiceAddition || '',
        rechnungsnummer: initialData?.invoiceNumber || 1,
        anzstellenrech: digitOptions.find(d => d.value === (initialData?.invoiceDigits || 4)) || digitOptions[2],
        kunden_zusatz: initialData?.customerAddition || '',
        kundennummer: initialData?.customerNumber || 1,
        anzstellenkunde: digitOptions.find(d => d.value === (initialData?.customerDigits || 4)) || digitOptions[2]
    });

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setIsSaving(true);

        try {
            const submitData = new FormData();
            submitData.append('angebot_zusatz', formData.angebot_zusatz);
            submitData.append('angebotsnummer', formData.angebotsnummer);
            submitData.append('anzstellenang', formData.anzstellenang?.value || 4);
            submitData.append('auftrag_zusatz', formData.auftrag_zusatz);
            submitData.append('auftragsnummer', formData.auftragsnummer);
            submitData.append('anzstellenab', formData.anzstellenab?.value || 4);
            submitData.append('rechnung_zusatz', formData.rechnung_zusatz);
            submitData.append('rechnungsnummer', formData.rechnungsnummer);
            submitData.append('anzstellenrech', formData.anzstellenrech?.value || 4);
            submitData.append('kunden_zusatz', formData.kunden_zusatz);
            submitData.append('kundennummer', formData.kundennummer);
            submitData.append('anzstellenkunde', formData.anzstellenkunde?.value || 4);
            submitData.append('ben_an_nummer', 0);
            submitData.append('ben_ab_nummer', 0);
            submitData.append('ben_re_nummer', 0);

            const response = await fetch('/index.php/settings/numbercircle_update', {
                method: 'POST',
                body: submitData
            });

            const data = await response.json();

            if (data.success) {
                showMessage('Nummernkreise erfolgreich gespeichert', 'success');
                onComplete();
            } else {
                showMessage('Fehler beim Speichern der Nummernkreise', 'error');
            }
        } catch (error) {
            console.error('Error saving number circles:', error);
            showMessage('Fehler beim Speichern der Nummernkreise', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const rows = [
        {
            label: 'Angebot',
            zusatzField: 'angebot_zusatz',
            nummerField: 'angebotsnummer',
            stellenField: 'anzstellenang',
            placeholder: 'z.B. AN-'
        },
        {
            label: 'Auftragsbestätigung',
            zusatzField: 'auftrag_zusatz',
            nummerField: 'auftragsnummer',
            stellenField: 'anzstellenab',
            placeholder: 'z.B. AB-'
        },
        {
            label: 'Rechnung',
            zusatzField: 'rechnung_zusatz',
            nummerField: 'rechnungsnummer',
            stellenField: 'anzstellenrech',
            placeholder: 'z.B. RE-'
        },
        {
            label: 'Kunde',
            zusatzField: 'kunden_zusatz',
            nummerField: 'kundennummer',
            stellenField: 'anzstellenkunde',
            placeholder: 'z.B. K-'
        }
    ];

    return (
        <div style={{ padding: '1.5rem' }}>
            <p style={{
                fontSize: '0.9375rem',
                color: 'var(--text-secondary, #64748b)',
                marginBottom: '0.5rem',
                lineHeight: '1.6'
            }}>
                Nummern für Angebote, Auftragsbestätigungen, Rechnungen oder Kontakte können vordefiniert werden.
                Dieser Schritt kann auch übersprungen werden.
            </p>
            <p style={{
                fontSize: '0.875rem',
                color: 'var(--text-secondary, #64748b)',
                marginBottom: '1.5rem',
                lineHeight: '1.6'
            }}>
                Die unten aufgeführten Variablen können in die Spalte <strong>Zusatz</strong> eingefügt werden.
            </p>

            {/* Number circles table */}
            <div style={{
                border: '1px solid var(--border-light, #e2e8f0)',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '1.5rem'
            }}>
                {/* Header */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 140px',
                    gap: '1rem',
                    padding: '0.75rem 1rem',
                    background: 'var(--bg-secondary, #f5f7f9)',
                    borderBottom: '1px solid var(--border-light, #e2e8f0)',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    color: 'var(--text-secondary, #64748b)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em'
                }}>
                    <div></div>
                    <div>Zusatz</div>
                    <div>Startwert</div>
                    <div>Anz. Stellen</div>
                </div>

                {/* Rows */}
                {rows.map((row, index) => (
                    <div
                        key={row.label}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr 140px',
                            gap: '1rem',
                            padding: '0.75rem 1rem',
                            alignItems: 'center',
                            borderBottom: index < rows.length - 1 ? '1px solid var(--border-light, #e2e8f0)' : 'none',
                            background: 'var(--bg-card, #ffffff)'
                        }}
                    >
                        <div style={{
                            fontWeight: '500',
                            fontSize: '0.9375rem',
                            color: 'var(--text-primary, #2c3e50)'
                        }}>
                            {row.label}
                        </div>
                        <div>
                            <input
                                type="text"
                                value={formData[row.zusatzField] || ''}
                                onChange={(e) => handleChange(row.zusatzField, e.target.value)}
                                style={{ ...inputStyle, fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                                placeholder={row.placeholder}
                            />
                        </div>
                        <div>
                            <input
                                type="number"
                                value={formData[row.nummerField] || 1}
                                onChange={(e) => handleChange(row.nummerField, parseInt(e.target.value) || 1)}
                                style={{ ...inputStyle, fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                                min="1"
                            />
                        </div>
                        <div>
                            <Select
                                value={formData[row.stellenField]}
                                onChange={(opt) => handleChange(row.stellenField, opt)}
                                options={digitOptions}
                                styles={customSelectStylesLarge}
                                isClearable={false}
                                isSearchable={false}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {/* Variables reference */}
            <div style={{
                border: '1px solid var(--border-light, #e2e8f0)',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '1.5rem'
            }}>
                <div style={{
                    padding: '0.75rem 1rem',
                    background: 'var(--bg-secondary, #f5f7f9)',
                    borderBottom: '1px solid var(--border-light, #e2e8f0)',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    color: 'var(--text-primary, #2c3e50)'
                }}>
                    Verfügbare Variablen für Zusatz
                </div>
                <div style={{ padding: '0.5rem 1rem' }}>
                    {variables.map((v, index) => (
                        <div
                            key={v.input}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 80px 80px',
                                gap: '1rem',
                                padding: '0.5rem 0',
                                borderBottom: index < variables.length - 1 ? '1px solid var(--border-light, #e2e8f0)' : 'none',
                                fontSize: '0.875rem'
                            }}
                        >
                            <span style={{ color: 'var(--text-primary, #2c3e50)' }}>{v.name}</span>
                            <code style={{
                                background: 'var(--bg-secondary, #f5f7f9)',
                                padding: '0.125rem 0.375rem',
                                borderRadius: '4px',
                                fontSize: '0.8125rem',
                                color: 'var(--accent-slate, #5b7c8d)',
                                textAlign: 'center'
                            }}>
                                {v.input}
                            </code>
                            <span style={{ color: 'var(--text-secondary, #64748b)', textAlign: 'center' }}>{v.example}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button
                    onClick={onComplete}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.625rem 1.5rem',
                        border: '1px solid var(--border-medium, #cbd5e1)',
                        borderRadius: '8px',
                        background: 'var(--bg-card, #ffffff)',
                        color: 'var(--text-secondary, #64748b)',
                        fontSize: '0.9375rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}
                >
                    Überspringen
                </button>
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.625rem 1.5rem',
                        border: 'none',
                        borderRadius: '8px',
                        background: 'var(--accent-slate, #5b7c8d)',
                        color: 'white',
                        fontSize: '0.9375rem',
                        fontWeight: '600',
                        cursor: isSaving ? 'wait' : 'pointer',
                        opacity: isSaving ? 0.7 : 1,
                        transition: 'all 0.2s ease'
                    }}
                >
                    {isSaving ? 'Speichert...' : 'Speichern & Weiter'}
                </button>
            </div>
        </div>
    );
};

export default StepNumberCircles;
